import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useOutletContext } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import Preview from '@renderer/components/Preview'
import { FaVideo, FaStop, FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import {
  stateUploader,
  processAndUploadSingleFile
} from '@renderer/utils/pipeline/outputOrchestrator'
import type { ProcessingContext } from '@renderer/types/outputPipeline.types'
import { useDesignLayout } from '@renderer/hooks'

interface UserContext {
  user?: {
    email: string
  }
}

const RecordPage: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const context = useOutletContext<UserContext | null>()
  const {
    setSessionState,
    userPreferences,
    id,
    gallery,
    galleryKey,
    logActivity
  } = useSessionStateStore()
  const { config: globalConfig, getFilter, filters } = useConfigStore()
  const { isCompact: recordCompact } = useDesignLayout()
  const recordConfig = globalConfig?.pages.record
  const [isRecording, setIsRecording] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [audioEnabled, setAudioEnabled] = useState(true)
  const [selectedMimeType, setSelectedMimeType] = useState('video/webm;codecs=vp8,opus')

  const selectedFilter = getFilter(userPreferences.filterId || filters[0]._id)
  const { downloadLocal, cameraSettings } = useSettingsStore()

  // Helper function to normalize captureRatio - 'auto' means no cropping
  const getNormalizedCaptureRatio = (): number => {
    if (cameraSettings.captureRatio === 'auto') return 16 / 9 // Default to 16:9 for auto
    if (typeof cameraSettings.captureRatio === 'number') return cameraSettings.captureRatio
    return 16 / 9 // Default fallback
  }

  const displayRatio = getNormalizedCaptureRatio()
  const canvas = document.querySelector('canvas')
  const recordingTimeout = recordConfig?.settings.recordingTimeout || 120
  const FPS = 30

  // Refs for recording
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordedChunksRef = useRef<Blob[]>([])
  const recordingTimer = useRef<NodeJS.Timeout | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  useEffect(() => {
    if (canvas) {
      // Upload session state on mount
      stateUploader.uploadSessionState()

      const setupRecorder = async (): Promise<void> => {
        try {
          // Get canvas stream
          const canvasStream = canvas.captureStream(FPS)

          // Get audio stream if enabled
          if (audioEnabled) {
            const audioStream = await navigator.mediaDevices.getUserMedia({
              audio: true,
              video: false
            })

            // Add audio track to canvas stream
            const audioTrack = audioStream.getAudioTracks()[0]
            canvasStream.addTrack(audioTrack)
          }

          streamRef.current = canvasStream

          // Check support for different codec combinations
          const codecOptions = [
            'video/mp4;codecs=h264,aac', // Alternative syntax
            'video/mp4;codecs=avc1.42E01E,mp4a.40.2', // H.264 + AAC
            'video/mp4;codecs=avc1.4d002a', // H.264 only
            'video/webm;codecs=h264,opus', // H.264 + Opus (fallback)
            'video/webm;codecs=vp8,opus' // WebM fallback
          ]

          let foundMimeType = ''
          for (const mimeType of codecOptions) {
            if (MediaRecorder.isTypeSupported(mimeType)) {
              console.log('Supported MIME type:', mimeType)
              foundMimeType = mimeType
              break
            }
          }

          if (!foundMimeType) {
            console.warn('No supported video MIME types found, falling back to default WebM')
            foundMimeType = 'video/webm;codecs=vp8,opus'
          }

          console.log('Using MIME type:', foundMimeType)
          setSelectedMimeType(foundMimeType)

          // Create MediaRecorder with selected format
          const mediaRecorder = new MediaRecorder(canvasStream, {
            mimeType: foundMimeType,
            videoBitsPerSecond: 2500000, // 2.5 Mbps
            audioBitsPerSecond: 128000 // 128 kbps
          })

          mediaRecorder.ondataavailable = (event: BlobEvent): void => {
            if (event.data.size > 0) {
              recordedChunksRef.current.push(event.data)
            }
          }

          mediaRecorderRef.current = mediaRecorder
        } catch (error) {
          console.error('Error setting up recorder:', error)
        }
      }

      setupRecorder()
    }

    return (): void => {
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop()
      }
      streamRef.current?.getTracks().forEach((track) => track.stop())
      if (recordingTimer.current) {
        clearInterval(recordingTimer.current)
      }
    }
  }, [canvas, audioEnabled])

  const startRecording = async (): Promise<void> => {
    if (!mediaRecorderRef.current) return

    try {
      setIsRecording(true)
      recordedChunksRef.current = []
      mediaRecorderRef.current.start(1000) // Collect data every second

      // Start timer
      const startTime = Date.now()
      recordingTimer.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000)
        setCurrentTime(elapsed)

        if (elapsed >= recordingTimeout) {
          stopRecording()
        }
      }, 1000)

      // Log recording start
      logActivity('Started recording.', {
        audioEnabled,
        fps: FPS
      })
    } catch (error) {
      console.error('Failed to start recording:', error)
      setIsRecording(false)
    }
  }

  const stopRecording = async (): Promise<void> => {
    if (!mediaRecorderRef.current || !isRecording) return

    try {
      mediaRecorderRef.current.stop()
      setIsRecording(false)

      if (recordingTimer.current) {
        clearInterval(recordingTimer.current)
      }

      // Wait for the last chunks to be processed
      await new Promise<void>((resolve) => {
        if (!mediaRecorderRef.current) return resolve()

        mediaRecorderRef.current.onstop = async (): Promise<void> => {
          const webmBlob = new Blob(recordedChunksRef.current, { type: selectedMimeType })
          const extension = selectedMimeType.startsWith('video/mp4') ? 'mp4' : 'webm'
          const webmUrl = URL.createObjectURL(webmBlob)

          // Handle local download if enabled
          if (downloadLocal) {
            const a = document.createElement('a')
            a.href = webmUrl
            a.download = `dreambooth-${id}-${Date.now()}.${extension}`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
          }

          // Create File object for upload
          const videoFile = new File([webmBlob], `dreambooth-${id}-video.${extension}`, {
            type: selectedMimeType
          })

          const ctx: ProcessingContext = {
            context,
            projectId,
            sessionId: id || '',
            galleryId: galleryKey || gallery.id,
            downloadLocal,
            isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true
          }

          const uploadResult = await processAndUploadSingleFile(videoFile, 'video', ctx)

          const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
          logActivity('Recording complete.', {
            duration: currentTime,
            url: uploadResult.uploadedUrl
          })
          setSessionState({
            gallery: {
              capturedItems: [
                ...prevItems,
                {
                  id: 'dreambooth-video-' + new Date().getTime(),
                  videoUrl: webmUrl
                }
              ]
            }
          })

          stateUploader.uploadSessionState()

          const selectEnabled = globalConfig?.pages.select?.settings?.enabled ?? false
          const editEnabled = globalConfig?.pages.edit?.settings?.enabled ?? false
          let nextPage = 'result'
          if (selectEnabled) {
            nextPage = 'select'
          } else if (editEnabled) {
            nextPage = 'edit'
          }
          navigate(`/${projectId}/${nextPage}`)
          resolve()
        }
      })
    } catch (error) {
      console.error('Failed to stop recording:', error)
    }
  }

  const toggleAudio = (): void => {
    if (isRecording) return
    setAudioEnabled(!audioEnabled)
  }

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  return (
    <div className="relative w-full h-full bg-carbon">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full">
        <Preview
          key={`${selectedFilter}-${cameraSettings.mirror}-${cameraSettings.previewRotation}-${getNormalizedCaptureRatio()}-${displayRatio}`}
          adjustments={selectedFilter?.adjustments}
          filter={selectedFilter?._id}
          ratio={displayRatio}
        />
      </div>

      {/* Recording Controls */}
      <div
        className={`absolute left-1/2 flex -translate-x-1/2 flex-col items-center ${recordCompact ? 'bottom-4 gap-3' : 'bottom-12 gap-6'}`}
      >
        <div className={`flex items-center ${recordCompact ? 'gap-3' : 'gap-8'}`}>
          <button
            onClick={toggleAudio}
            disabled={isRecording}
            className={`rounded-full ${recordCompact ? 'p-4' : 'p-6'} ${
              audioEnabled ? 'bg-red-500' : 'bg-gray-600'
            } transition-colors duration-200 ${isRecording ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {audioEnabled ? (
              <FaMicrophone className={`text-white ${recordCompact ? 'text-2xl' : 'text-3xl'}`} />
            ) : (
              <FaMicrophoneSlash className={`text-white ${recordCompact ? 'text-2xl' : 'text-3xl'}`} />
            )}
          </button>

          {!isRecording ? (
            <button
              title="Start Recording"
              onClick={startRecording}
              className={`rounded-full bg-red-500 transition-colors duration-200 hover:bg-red-600 ${recordCompact ? 'p-4' : 'p-8'}`}
            >
              <FaVideo className={`text-white ${recordCompact ? 'text-2xl' : 'text-4xl'}`} />
            </button>
          ) : (
            <button
              title="Stop Recording"
              onClick={stopRecording}
              className={`animate-pulse rounded-full bg-red-500 transition-colors duration-200 hover:bg-red-600 ${recordCompact ? 'p-4' : 'p-8'}`}
            >
              <FaStop className={`text-white ${recordCompact ? 'text-2xl' : 'text-4xl'}`} />
            </button>
          )}
        </div>

        <div
          className={`flex items-center gap-2 rounded-full bg-black/50 ${recordCompact ? 'px-3 py-1' : 'px-6 py-3'}`}
        >
          <div
            className={`w-3 h-3 rounded-full ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-gray-500'}`}
          />
          <span
            className={`font-medium font-mono tabular-nums text-white ${recordCompact ? 'text-lg' : 'text-2xl'}`}
          >
            {formatTime(currentTime)} / {formatTime(recordingTimeout)}
          </span>
        </div>
      </div>
    </div>
  )
}

export default RecordPage
